import { useState } from "react";
import { Reveal } from "@/components/Reveal";
import { Plus } from "lucide-react";
import { PANEL, TICKET } from "@/data";

const HostCard = ({ host, large, delay }) => {
  const [open, setOpen] = useState(false);

  return (
    <Reveal delay={delay} className="flex flex-col items-center text-center">
      <div
        data-testid={`host-card-${host.name}`}
        className="group relative flex flex-col items-center text-center"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <button
          type="button"
          aria-label={`Zobrazit informace o ${host.name}`}
          onClick={() => setOpen((v) => !v)}
          className="relative focus:outline-none"
        >
          {host.tag && (
            <span className="absolute -top-2 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full bg-[var(--forest)] px-3 py-1 text-[11px] font-semibold text-white shadow">
              {host.tag}
            </span>
          )}
          <div
            className={`overflow-hidden rounded-full ring-4 ring-[var(--cream-soft)] shadow-md transition-transform duration-300 group-hover:scale-[1.03] ${
              large ? "h-44 w-44 md:h-52 md:w-52" : "h-36 w-36 md:h-40 md:w-40"
            }`}
          >
            <img
              src={host.image}
              alt={host.name}
              className="h-full w-full object-cover"
            />
          </div>
          <span
            className={`absolute bottom-1 right-1 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-[var(--forest)] text-white shadow-md ring-2 ring-[var(--cream)] transition-transform duration-300 ${
              open ? "rotate-45" : ""
            }`}
          >
            <Plus className="h-4 w-4" />
          </span>
        </button>

        <h3 className="mt-5 font-display text-base md:text-lg font-semibold text-[var(--ink)]">
          {host.name}
        </h3>
        <p className="mt-1 text-sm text-[var(--muted)]">{host.role}</p>

        <div
          data-testid={`host-bio-${host.name}`}
          className={`absolute top-1/2 left-1/2 z-40 w-[330px] max-w-[86vw] -translate-x-1/2 -translate-y-1/2 rounded-3xl bg-[var(--forest)] p-6 text-left shadow-2xl transition-all duration-300 ${
            open
              ? "scale-100 opacity-100"
              : "pointer-events-none scale-95 opacity-0"
          }`}
        >
          <h4 className="font-display text-lg font-bold text-white">
            {host.name}
          </h4>
          <span className="mt-3 mb-4 block h-px w-12 bg-white/30" />
          <p className="text-sm leading-relaxed text-white/90">{host.bio}</p>
          <p className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--lime)]">
            {host.role}
          </p>
        </div>
      </div>
    </Reveal>
  );
};

export const Panel = () => {
  const [featured, ...rest] = PANEL.hosts;
  return (
    <section id="panel" className="bg-[var(--cream-soft)]/60 py-20 md:py-28">
      <div className="mx-auto max-w-5xl px-6">
        <Reveal>
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-[var(--accent)]">
            {PANEL.eyebrow}
          </p>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[var(--ink)]">
            {PANEL.title}
          </h2>
        </Reveal>
        <Reveal delay={0.14}>
          <p className="mt-4 max-w-2xl text-base md:text-lg text-[var(--muted)]">
            {PANEL.description}
          </p>
        </Reveal>

        <div
          data-testid="panel-hosts"
          className="mt-16 flex flex-col items-center gap-y-28"
        >
          <HostCard host={featured} large delay={0.1} />
          <div className="grid w-full grid-cols-1 gap-x-10 gap-y-28 sm:grid-cols-2">
            {rest.map((host, i) => (
              <HostCard key={host.name} host={host} delay={0.08 * i} />
            ))}
          </div>
        </div>

        <Reveal delay={0.1}>
          <div
            data-testid="panel-ticket"
            className="mx-auto mt-16 flex max-w-2xl flex-col items-center gap-7 rounded-3xl border border-[var(--forest)]/15 bg-[var(--cream-card)] p-8 text-center shadow-sm sm:flex-row sm:gap-9 sm:text-left"
          >
            <div className="shrink-0 rounded-2xl bg-white p-3 border-2 border-[var(--forest)]">
              <img
                src={TICKET.qr}
                alt="QR kód pro vstupenku"
                data-testid="ticket-qr"
                className="h-40 w-40"
              />
            </div>
            <div>
              <h3 className="font-display text-2xl font-bold leading-snug text-[var(--ink)]">
                {TICKET.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-[var(--muted)]">
                {TICKET.note}
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
